// Define the RoastData interface
import { env } from '../env';
import { logger } from './loggerService';

export interface RoastData {
  asin: string;
  product_name: string;
  product_image: string;
  listing_screenshot: string;
  marketplace?: string;
  price?: string;
  rating?: number;
  review_count?: number;
  likes?: number;
  dislikes?: number;
  shares?: number;
  improvements?: string[];
  analysis: {
    roast_level: 'mild' | 'medium' | 'spicy' | 'nuclear';
    roast_text: string;
    score: number;
    strengths: string[];
    weaknesses: string[];
    metrics: {
      title_score: number;
      images_score: number;
      description_score: number;
      price_score: number;
      bullet_points_score: number;
      reviews_score: number;
      [key: string]: number;
    };
    priority_actions: string[];
    improvements?: string[];
    competitive_insight: string;
    compliance_alerts: string[];
    title_analysis?: string;
    bullet_points_analysis?: string;
    description_analysis?: string;
    images_analysis?: string;
    pricing_analysis?: string;
    reviews_analysis?: string;
    overall_analysis?: string;
  };
  created_at?: string;
  fromDatabase?: boolean;
}

// Always use relative URLs for API requests
const API_BASE_URL = '/api';
const ANALYZE_TIMEOUT_MS = 300000;

// Force all requests to use the current origin by disabling any URL with domain
const getApiUrl = (path: string) => {
  return `${API_BASE_URL}${path}`;
};

/**
 * Extracts ASIN from a given Amazon URL or ASIN
 * @param urlOrAsin Amazon URL or ASIN
 * @returns ASIN if found, otherwise null
 */
function extractAsin(urlOrAsin: string): string | null {
  if (urlOrAsin.startsWith('http')) {
    const asinPattern = /(?:\/dp\/|\/gp\/product\/|\/ASIN\/|\/asin\/)([A-Z0-9]{10})/i;
    const match = urlOrAsin.match(asinPattern);
    return match ? match[1] : null;
  }
  return urlOrAsin;
}

/**
 * Send a product URL or ASIN for analysis
 * First checks if a recent analysis (less than 72 hours old) exists in the database
 * If not, sends the request to the Python analyzer for a new analysis
 */
export async function analyzeProduct(urlOrAsin: string, roastLevel: string = 'medium', marketplace: string = 'com'): Promise<RoastData> {
  try {
    console.log(`Analyzing product: ${urlOrAsin}`);
    
    // Extract ASIN if given a full Amazon URL
    const asin = extractAsin(urlOrAsin);
    
    if (!asin) {
      throw new Error('Invalid Amazon URL or ASIN. Please enter a valid Amazon product URL or 10-character ASIN.');
    }
    
    // RoastForm already owns the cached-analysis lookup. This call should only
    // trigger a fresh analysis request.
    console.log('Sending ASIN to Python analyzer');
    
    // Send the request directly to the Amazon Analyzer
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), ANALYZE_TIMEOUT_MS);
    let response: Response;
    try {
      response = await fetch(getApiUrl(`/analyze`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          url_or_asin: asin,
          roast_level: roastLevel,
          marketplace: marketplace
        }),
        signal: controller.signal
      });
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error?.name === 'AbortError') {
        throw new Error('Analysis request timed out. Please try again.');
      }
      throw new Error(`Network error while contacting analyzer: ${error?.message || 'unknown error'}`);
    }
    clearTimeout(timeoutId);

    if (!response.ok) {
      const requestId = response.headers.get('X-Request-Id');
      const responseText = await response.text();
      let parsedError: any = null;
      try {
        parsedError = responseText ? JSON.parse(responseText) : null;
      } catch (_error) {
        parsedError = null;
      }

      const errorMessage = parsedError?.error || `Failed to analyze product (HTTP ${response.status})`;
      const errorCode = parsedError?.code ? ` [${parsedError.code}]` : '';
      const errorRequestId = parsedError?.request_id || requestId;
      const responseSnippet = parsedError ? '' : ` Response: ${responseText.slice(0, 200)}`;
      const requestSuffix = errorRequestId ? ` Request ID: ${errorRequestId}` : '';
      throw new Error(`${errorMessage}${errorCode}.${requestSuffix}${responseSnippet}`.trim());
    }
    
    let data: RoastData & { request_id?: string };
    try {
      data = await response.json();
    } catch (_error) {
      throw new Error(`Analyzer returned a non-JSON success response. Request ID: ${response.headers.get('X-Request-Id') || 'unknown'}`);
    }
    
    // Map any backend roast levels to frontend expected values
    if (data.analysis && data.analysis.roast_level) {
      const backendRoastLevel = data.analysis.roast_level as string;
      if (backendRoastLevel === 'light') data.analysis.roast_level = 'mild';
      if (backendRoastLevel === 'dark') data.analysis.roast_level = 'spicy';
      if (backendRoastLevel === 'burnt') data.analysis.roast_level = 'nuclear';
    }
    
    // Ensure listing_screenshot is present
    if (!data.listing_screenshot) {
      data.listing_screenshot = data.product_image;
    }
    
    return data;
  } catch (error) {
    console.error('Error analyzing product:', error);
    throw error;
  }
}

/**
 * Convert database response format to RoastData format
 */
const formatDatabaseResponseToRoastData = (dbData: any): RoastData => {
  if (!dbData) {
    console.error('Database data is null or undefined');
    return {
      asin: '',
      product_name: '',
      product_image: '',
      listing_screenshot: '',
      analysis: {
        roast_level: 'medium',
        roast_text: '',
        score: 0,
        strengths: [],
        weaknesses: [],
        metrics: {
          title_score: 0,
          images_score: 0,
          description_score: 0,
          price_score: 0,
          bullet_points_score: 0,
          reviews_score: 0
        },
        priority_actions: [],
        competitive_insight: '',
        compliance_alerts: []
      }
    };
  }

  console.log('Formatting database response:', dbData);
  
  // Parse improvements/priority_actions if it's a string
  let priority_actions = dbData.priority_actions || dbData.improvements || [];
  if (typeof priority_actions === 'string') {
    try {
      priority_actions = JSON.parse(priority_actions);
      console.log('Parsed priority_actions:', priority_actions);
    } catch (e) {
      console.error('Error parsing priority_actions:', e);
      priority_actions = [];
    }
  }
  
  // Parse strengths if it's a string
  let strengths = dbData.strengths || [];
  if (typeof strengths === 'string') {
    try {
      strengths = JSON.parse(strengths);
      console.log('Parsed strengths:', strengths);
    } catch (e) {
      console.error('Error parsing strengths:', e);
      strengths = [];
    }
  }
  
  // Parse weaknesses if it's a string
  let weaknesses = dbData.weaknesses || [];
  if (typeof weaknesses === 'string') {
    try {
      weaknesses = JSON.parse(weaknesses);
      console.log('Parsed weaknesses:', weaknesses);
    } catch (e) {
      console.error('Error parsing weaknesses:', e);
      weaknesses = [];
    }
  }
  
  // Parse compliance_alerts if it's a string
  let compliance_alerts = dbData.compliance_alerts || [];
  if (typeof compliance_alerts === 'string') {
    try {
      compliance_alerts = JSON.parse(compliance_alerts);
      console.log('Parsed compliance_alerts:', compliance_alerts);
    } catch (e) {
      console.error('Error parsing compliance_alerts:', e);
      compliance_alerts = [];
    }
  }
  
  // Parse metrics if it's a string
  let metrics = dbData.metrics || {
    title_score: 0,
    images_score: 0,
    description_score: 0,
    price_score: 0,
    bullet_points_score: 0,
    reviews_score: 0
  };
  if (typeof metrics === 'string') {
    try {
      metrics = JSON.parse(metrics);
      console.log('Parsed metrics:', metrics);
    } catch (e) {
      console.error('Error parsing metrics:', e);
      metrics = {
        title_score: 0,
        images_score: 0,
        description_score: 0,
        price_score: 0,
        bullet_points_score: 0,
        reviews_score: 0
      };
    }
  }
  
  // Parse score if it's a string
  let score = dbData.score || 0;
  if (typeof score === 'string') {
    try {
      score = parseInt(score);
      if (isNaN(score)) score = 0;
      console.log('Parsed score:', score);
    } catch (e) {
      console.error('Error parsing score:', e);
      score = 0;
    }
  }
  
  // Handle different response formats
  if (dbData.analysis && typeof dbData.analysis === 'object') {
    // If already in the right format, just return it
    console.log('Data already has analysis object');
    return {
      ...dbData,
      analysis: {
        ...dbData.analysis,
        strengths: dbData.analysis.strengths || [],
        weaknesses: dbData.analysis.weaknesses || [],
        priority_actions: dbData.analysis.priority_actions || priority_actions,
        compliance_alerts: dbData.analysis.compliance_alerts || compliance_alerts,
        metrics: dbData.analysis.metrics || metrics
      }
    };
  }
  
  // Create a properly formatted RoastData object
  console.log('Creating new RoastData object from database response');
  const result = {
    asin: dbData.asin || '',
    product_name: dbData.product_name || '',
    product_image: dbData.product_image || '',
    listing_screenshot: dbData.listing_screenshot || dbData.product_image || '',
    price: dbData.price || '',
    rating: typeof dbData.rating === 'number' ? dbData.rating : 0,
    review_count: typeof dbData.review_count === 'number' ? dbData.review_count : 0,
    likes: typeof dbData.likes === 'number' ? dbData.likes : 0,
    dislikes: typeof dbData.dislikes === 'number' ? dbData.dislikes : 0,
    shares: typeof dbData.shares === 'number' ? dbData.shares : 0,
    created_at: dbData.created_at || new Date().toISOString(),
    analysis: {
      roast_level: dbData.roast_level || 'medium',
      roast_text: dbData.roast_text || '',
      score: score,
      strengths: strengths,
      weaknesses: weaknesses,
      metrics: metrics,
      priority_actions: priority_actions,
      competitive_insight: dbData.competitive_insight || '',
      compliance_alerts: compliance_alerts
    }
  };
  
  console.log('Final formatted data has priority_actions:', result.analysis.priority_actions && Array.isArray(result.analysis.priority_actions));
  return result;
};

export async function getRoastByShareId(shareId: string): Promise<RoastData | null> {
  try {
    console.log(`Fetching roast data by shareId: ${shareId}`);
    
    // First try the new API endpoint that has the improved priority_actions handling
    const newApiResponse = await fetch(`/api/share/${shareId}`);
    
    if (newApiResponse.ok) {
      console.log("New API endpoint returned success");
      const data = await newApiResponse.json();
      console.log('Received data from new API:', JSON.stringify(data, null, 2));
      
      // If the data already has the correct structure, just return it
      if (data.analysis && typeof data.analysis === 'object') {
        console.log("Data has the correct structure");
        return processRoastData(data);
      }
      
      return formatDatabaseResponseToRoastData(data);
    }
    
    console.log("New API endpoint failed, falling back to old endpoint");
    
    // Fall back to the original endpoint
    const response = await fetch(`${API_BASE_URL}/share/${shareId}`);
    
    if (!response.ok) {
      console.error(`Error fetching roast by shareId: ${response.status} ${response.statusText}`);
      return null;
    }
    
    const data = await response.json();
    console.log('Received data from API:', JSON.stringify(data, null, 2));
    
    return processRoastData(data);
  } catch (error) {
    console.error('Error fetching roast by shareId:', error);
    return null;
  }
}

// Helper function to process and normalize roast data
function processRoastData(data: any): RoastData {
  // If the data already has the correct structure, process its fields
  if (data.analysis && typeof data.analysis === 'object') {
    const analysis = data.analysis;
    
    // Ensure priority_actions is properly extracted
    if (!analysis.priority_actions) {
      // Try to use improvements field from analysis
      if (analysis.improvements) {
        console.log('Found improvements in analysis but no priority_actions, copying improvements to priority_actions');
        analysis.priority_actions = analysis.improvements;
      }
      // Try to use improvements field from top level
      else if (data.improvements) {
        console.log('Found top-level improvements but no priority_actions, copying improvements to priority_actions');
        analysis.priority_actions = data.improvements;
      }
      // If still not found, initialize empty array
      else {
        analysis.priority_actions = [];
      }
    }
    
    // Handle priority_actions as JSON string
    if (typeof analysis.priority_actions === 'string') {
      try {
        analysis.priority_actions = JSON.parse(analysis.priority_actions);
        console.log('Parsed priority_actions from JSON string:', analysis.priority_actions);
      } catch (e) {
        console.error('Error parsing priority_actions:', e);
        analysis.priority_actions = [analysis.priority_actions];
      }
    }
    
    // Ensure priority_actions is always an array
    if (!Array.isArray(analysis.priority_actions)) {
      if (analysis.priority_actions) {
        analysis.priority_actions = [String(analysis.priority_actions)];
      } else {
        analysis.priority_actions = [];
      }
    }
    
    // Handle strengths and weaknesses
    if (!analysis.strengths) {
      analysis.strengths = [];
    }
    
    if (!analysis.weaknesses) {
      analysis.weaknesses = [];
    }
    
    return data;
  }
  
  // Otherwise format it to match the RoastData interface
  return formatDatabaseResponseToRoastData(data);
}

export async function getRoastByAsin(asin: string): Promise<RoastData | null> {
  try {
    logger.log(`Fetching roast data by ASIN: ${asin}`);
    const response = await fetch(`${API_BASE_URL}/roast/${asin}`);
    
    if (!response.ok) {
      logger.error(`Error fetching roast by ASIN: ${response.status} ${response.statusText}`);
      return null;
    }
    
    const data = await response.json();
    console.log('Received data from API:', JSON.stringify(data, null, 2));
    
    // If the data already has the correct structure, just return it
    if (data.analysis && typeof data.analysis === 'object') {
      // Ensure priority_actions is properly extracted
      if (!data.analysis.priority_actions && data.analysis.improvements) {
        console.log('Found improvements in analysis but no priority_actions, copying improvements to priority_actions');
        data.analysis.priority_actions = data.analysis.improvements;
      }
      
      // Also check for top-level improvements field (from database format)
      if (!data.analysis.priority_actions && data.improvements) {
        console.log('Found top-level improvements but no priority_actions, copying improvements to priority_actions');
        data.analysis.priority_actions = data.improvements;
      }
      
      // Check if priority_actions exists but is a string (JSON string)
      if (typeof data.analysis.priority_actions === 'string') {
        try {
          data.analysis.priority_actions = JSON.parse(data.analysis.priority_actions);
          logger.log('Parsed priority_actions from JSON string:', data.analysis.priority_actions);
        } catch (e) {
          logger.error('Error parsing priority_actions:', e);
          data.analysis.priority_actions = [];
        }
      }
      
      // Ensure priority_actions is always an array
      if (!data.analysis.priority_actions) {
        data.analysis.priority_actions = [];
      }
      
      return data;
    }
    
    // Otherwise format it to match the RoastData interface
    return formatDatabaseResponseToRoastData(data);
  } catch (error) {
    logger.error('Error fetching roast by ASIN:', error);
    return null;
  }
}

// Mock data for testing
export const getMockWebhookResponse = (asin: string): RoastData => ({
  asin: asin,
  product_name: "Amazon Basics 8-Sheet Capacity, Cross-Cut Paper Shredder",
  product_image: "https://m.media-amazon.com/images/I/71zny7BTRlL._AC_SL1500_.jpg",
  listing_screenshot: "https://m.media-amazon.com/images/I/71zny7BTRlL._AC_SL1500_.jpg",
  analysis: {
    roast_level: "medium",
    roast_text: "Ah, the paper shredder that promises to protect your secrets but works about as fast as a sloth on vacation. It's like having a very determined hamster chewing through your documents!",
    score: 75,
    strengths: [
      "Clear product title with key specifications",
      "Good quality main product image",
      "Competitive pricing in its category"
    ],
    weaknesses: [
      "Limited capacity might not suit office needs",
      "Could use more lifestyle usage photos",
      "Description lacks comparison with similar models"
    ],
    metrics: {
      title_score: 80,
      images_score: 70,
      description_score: 65,
      price_score: 75,
      bullet_points_score: 85,
      reviews_score: 80
    },
    priority_actions: [
      "Add comparison table with higher capacity models",
      "Include more lifestyle images showing the product in use",
      "Enhance description with specific use cases"
    ],
    competitive_insight: "This entry-level shredder competes well on price but lacks the capacity features of mid-tier competitors.",
    compliance_alerts: []
  }
});
